import React, {useCallback, useEffect, useMemo, useState} from 'react';


import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getAccountsByName} from 'src/openapi/admin/apis/GetAccountsByNameApi';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';

import {AccountsList} from './accounts-list';

import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';
import type {Error} from 'src/openapi/admin/models/Error';

// eslint-disable-next-line no-useless-escape
const SEARCH_REGEX = /[^a-zA-Z0-9 &'.+_,\(\)\[\]-]+/g;
const MINIMUM_QUERY_ERROR = 'The search query is less than the minimum allowed, continue typing...';
const INVALID_QUERY_ERROR = 'The search query contains invalid characters, please remove them and try again...';

type Props = {
    disabled?: boolean,
    selectedAccount: AccountShortInfo | null,
    onChange: (account: AccountShortInfo | null) => void,
};

export const AccountSearch = ({selectedAccount, onChange, disabled}: Props) => {
    const apiDispatch = useAppDispatch();

    const [query, setQuery] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState<Error | null>(null);
    const [suggestions, setSuggestions] = useState<AccountShortInfo[]>([]);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        if (selectedAccount?.accountName) {
            setQuery(selectedAccount.accountName);
        } else {
            setQuery('');
        }
    }, [selectedAccount]);

    const sanitizedQuery = useMemo(
        () => query.replace(SEARCH_REGEX, ''),
        [query],
    );

    const loadSuggestions = useCallback(() => {
        setLoading(true);
        apiDispatch(getAccountsByName({accountName: sanitizedQuery}))
            .then(res => {
                if (res.payload.success) {
                    setSuggestions(res.payload.data);
                    setLoadingError(null);
                } else if (res.payload.error) {
                    setLoadingError(res.payload.data);
                    setSuggestions([]);
                }
                setLoading(false);
            });
    }, [apiDispatch, sanitizedQuery]);

    useEffectWithDeps((prev, current) => {
        if (prev.sanitizedQuery !== current.sanitizedQuery) {
            if (sanitizedQuery.length > 2) {
                loadSuggestions();
            } else {
                setSuggestions([]);
            }
        }
    }, {sanitizedQuery});

    const onInputClear = useCallback(() => {
        setQuery('');
        setSuggestions([]);
        setShown(false);
        onChange(null);
    }, [onChange]);

    const onItemSelect = useCallback((account: AccountShortInfo) => {
        setQuery(account.accountName);
        onChange(account);
        setSuggestions([]);
        setShown(false);
    }, [onChange]);

    const onClick = useCallback(() => {
        setShown(true);
        if (sanitizedQuery.length > 2) {
            loadSuggestions();
        }
    }, [sanitizedQuery, loadSuggestions]);

    const onChangeQuery = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
        onChange(null);
    }, [onChange]);

    const noResultsMessage = useMemo(() => {
        if (sanitizedQuery !== query) {
            return INVALID_QUERY_ERROR;
        } else if (query.length < 3) {
            return MINIMUM_QUERY_ERROR;
        }
        return undefined;
    }, [query, sanitizedQuery]);

    return (
        <div style={{width: 300}}>
            <Autosuggest
                value={query}
                disabled={disabled}
                type="ABSOLUTE"
                maxLength={255}
                onChange={onChangeQuery}
                onClear={onInputClear}
                onClick={onClick}
                shown={shown}
                setShown={setShown}
                onItemSelect={onItemSelect}
                placeholder="Search for accounts">
                <AccountsList
                    id="account-search-list"
                    isLoading={isLoading}
                    accounts={suggestions}
                    error={loadingError}
                    noResultsMessage={noResultsMessage}
                />
            </Autosuggest>
        </div>
    );
};
